import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faTimes } from "@fortawesome/free-solid-svg-icons";
import { colors, constants } from "utils";
import { request } from "hooks/useOkoApi/useOkoApi";
import { clearAllFilters } from "utils/persistFilters";
import { useImpersonation } from "hooks/useImpersonation/useImpersonation";
import { logError, LogCategory } from "utils/sentryLogger";

const formatTeacher = (u) => {
  if (!u) return "";
  const name = [u.first_name, u.last_name].filter(Boolean).join(" ").trim();
  if (name) return name;
  return u.display_name || u.email || "Unnamed teacher";
};

export const TeacherSelector = ({ currentUserEmail }) => {
  const { impersonatedUser, setImpersonatedUser, stopImpersonating } = useImpersonation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  // null = not yet checked, true = allowed, false = not allowed (403 from API).
  // The /impersonatable-teachers endpoint is the source of truth for
  // who can impersonate, so we let the backend decide and hide the
  // component when it says no.
  const [allowed, setAllowed] = useState(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchText("");
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const fetchTeachers = useCallback(async (query) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("limit", "20");
      if (query) params.append("search", query);
      const response = await request(`account/educator/impersonatable-teachers?${params.toString()}`, { method: "GET", noImpersonation: true });
      setResults(response.teachers || []);
      setAllowed(true);
    } catch (e) {
      // A 403 here just means the caller isn't permitted to impersonate —
      // hide the component silently. Anything else is a real error worth
      // logging.
      if (e?.status === 403) {
        setAllowed(false);
        setResults([]);
        return;
      }
      logError("Failed to load teachers for impersonation dropdown", e, {
        category: LogCategory.ERROR,
      });
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Probe permission once on mount so we know whether to render anything.
  useEffect(() => {
    fetchTeachers("");
  }, [fetchTeachers]);

  useEffect(() => {
    if (isOpen) fetchTeachers(searchText);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const onSearchChange = (e) => {
    const val = e.target.value;
    setSearchText(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchTeachers(val), 300);
  };

  // Switching the impersonated user changes the X-Impersonate-User-Id header
  // that every educator request reads from localStorage. A hard reload is the
  // simplest way to guarantee all pages refetch as the new teacher. We navigate
  // to the educator home so the destination always mounts fresh (avoids landing
  // on a deep-linked page whose cached state belonged to the previous teacher),
  // and only after the localStorage write has been committed. We also clear the
  // persisted report filters, which are global (not per-teacher), so the new
  // teacher's pages don't inherit the previous teacher's class/week/student
  // selections — that mismatch leaves reports blank until a filter is touched.
  const reloadAsImpersonatedUser = () => {
    clearAllFilters();
    window.location.assign(constants.EDUCATOR_PATH);
  };

  const selectTeacher = (user) => {
    setImpersonatedUser(user);
    setIsOpen(false);
    setSearchText("");
    reloadAsImpersonatedUser();
  };

  const handleStop = (e) => {
    e.stopPropagation();
    stopImpersonating();
    reloadAsImpersonatedUser();
  };

  const displayText = useMemo(() => {
    if (impersonatedUser) return formatTeacher(impersonatedUser);
    return currentUserEmail || "Select teacher";
  }, [impersonatedUser, currentUserEmail]);

  if (allowed === false) return null;

  return (
    <Wrapper ref={containerRef}>
      <Label>Teacher</Label>
      {isOpen ? (
        <SelectShell $focused>
          <SearchInput
            ref={inputRef}
            value={searchText}
            onChange={onSearchChange}
            placeholder="Type to search"
          />
          <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: "10px", color: colors.blue300 }} />
        </SelectShell>
      ) : (
        <SelectButton onClick={() => setIsOpen(true)}>
          <SelectText>{displayText}</SelectText>
          {impersonatedUser ? (
            <ClearBtn onClick={handleStop} title="Stop impersonating">
              <FontAwesomeIcon icon={faTimes} style={{ fontSize: "12px", color: colors.milkGlass950 }} />
            </ClearBtn>
          ) : (
            <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: "10px", color: colors.blue300 }} />
          )}
        </SelectButton>
      )}

      {isOpen && (
        <Menu>
          {currentUserEmail && (
            <Item
              $active={!impersonatedUser}
              onClick={() => {
                if (impersonatedUser) {
                  stopImpersonating();
                  setIsOpen(false);
                  setSearchText("");
                  reloadAsImpersonatedUser();
                } else {
                  setIsOpen(false);
                  setSearchText("");
                }
              }}
            >
              {currentUserEmail} (you)
            </Item>
          )}
          {loading ? (
            <Loading>Searching…</Loading>
          ) : results.length === 0 ? (
            <NoResults>
              <NoResultsTitle>No teachers found</NoResultsTitle>
              <NoResultsText>Try a different keyword or check your spelling.</NoResultsText>
            </NoResults>
          ) : (
            results
              .filter((u) => u.email !== currentUserEmail)
              .map((u) => (
                <Item
                  key={u.id}
                  $active={impersonatedUser?.id === u.id}
                  onClick={() => selectTeacher(u)}
                >
                  <ItemPrimary>{formatTeacher(u)}</ItemPrimary>
                  {u.email && <ItemSecondary>{u.email}</ItemSecondary>}
                </Item>
              ))
          )}
        </Menu>
      )}

    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  padding: 0 16px;
`;

const Label = styled.label`
  display: block;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: ${colors.nightlyWoods950};
  margin-bottom: 6px;
`;

const SelectButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px 12px;
  background: white;
  border: 1px solid ${colors.blue300};
  border-radius: 6px;
  cursor: pointer;
  min-height: 40px;
  gap: 8px;

  &:hover { border-color: ${colors.blue400}; }
`;

const SelectShell = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px 12px;
  background: white;
  border: 1px solid ${(props) => (props.$focused ? colors.blue500 : colors.blue300)};
  border-radius: 6px;
  min-height: 40px;
  gap: 8px;
`;

const SelectText = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 14px;
  color: ${colors.nightlyWoods500};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
  flex: 1;
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  &:focus:not(:focus-visible) {
    outline: none;
  }
  font-family: "Inter", sans-serif;
  font-size: 14px;
  color: ${colors.nightlyWoods500};
  background: transparent;
  min-width: 0;
  &::placeholder { color: ${colors.milkGlass950}; }
`;

const ClearBtn = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Menu = styled.div`
  position: absolute;
  top: 70px;
  left: 16px;
  right: 16px;
  margin-top: 4px;
  background: white;
  border: 1px solid #BAB9F5;
  border-radius: 8px;
  box-shadow: 0px 4px 12px rgba(14, 26, 49, 0.12);
  z-index: 200;
  max-height: 320px;
  overflow-y: auto;
`;

const Item = styled.div`
  padding: 8px 16px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  color: ${colors.nightlyWoods500};
  cursor: pointer;
  background: ${(props) => (props.$active ? colors.blue50 : "transparent")};
  &:hover { background: ${colors.neutral100}; }
`;

const ItemPrimary = styled.div`
  font-weight: 500;
  line-height: 20px;
`;

const ItemSecondary = styled.div`
  font-size: 12px;
  color: ${colors.milkGlass950};
  line-height: 16px;
`;

const Loading = styled.div`
  padding: 12px 16px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  color: ${colors.milkGlass950};
`;

const NoResults = styled.div`
  padding: 8px 16px;
`;

const NoResultsTitle = styled.div`
  font-family: "ABC Marfa", sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #858B87;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
`;

const NoResultsText = styled.div`
  font-family: "Inter", sans-serif;
  font-size: 14px;
  color: #002E19;
`;

